export class RichTextHelper {
  static readonly MAX_BYTES = 60000;
  private static readonly KNOWN_TLDS = new Set([
    'com', 'com.br', 'br', 'gov', 'gov.br', 'org', 'org.br', 'net', 'net.br',
    'edu', 'edu.br', 'ai', 'me', 'io', 'app', 'dev', 'to', 'info', 'co', 'link',
    'uk', 'us', 'eu', 'pt', 'fr', 'de', 'es', 'it', 'biz', 'pro', 'tv', 'mil',
  ]);
  private static readonly TAGS = new Set(['P', 'DIV', 'BR', 'B', 'STRONG', 'I', 'EM', 'U', 'UL', 'OL', 'LI', 'A']);
  private static readonly DROP = new Set([
    'SCRIPT', 'STYLE', 'INPUT', 'BUTTON', 'IMG', 'SVG', 'TEXTAREA', 'SELECT', 'IFRAME',
    'OBJECT', 'EMBED', 'LINK', 'META', 'NOSCRIPT', 'TEMPLATE', 'FORM', 'VIDEO', 'AUDIO', 'CANVAS', 'MATH', 'HEAD', 'TITLE',
  ]);
  private static readonly LIST_CLASSES = new Set([
    'rte-list-alpha-paren', 'rte-list-upper-alpha-paren', 'rte-list-lower-roman-paren',
    'rte-list-upper-roman-paren', 'rte-list-decimal-paren', 'rte-list-decimal-ordm', 'rte-list-decimal-ordf',
  ]);
  private static readonly LIST_STYLES = new Set(['decimal', 'lower-alpha', 'upper-alpha', 'lower-roman', 'upper-roman', 'disc', 'circle', 'square']);

  static normalizeUrl(value: string, allowInternal = false): string | null {
    const raw = value.trim();
    if (!raw || /[\s<>"'\u0000-\u001f\u007f\\]/.test(raw)) return null;
    if (allowInternal && /^\/unidade\/solicitacao\/detalhes\?codsolicitacao=\d+$/.test(raw)) return raw;
    if (/^mailto:/i.test(raw)) {
      return /^mailto:[^@?]+@[a-z\d](?:[a-z\d.-]*[a-z\d])?\.[a-z]{2,}(?:\?[^\s]*)?$/i.test(raw)
        && !raw.split('@')[1].split('?')[0].includes('..') ? `mailto:${raw.slice(7)}` : null;
    }
    if (/^tel:/i.test(raw)) return /^tel:\+?[\d().-]+(?:;ext=\d+)?$/i.test(raw) && /\d/.test(raw.slice(4).split(';')[0]) ? `tel:${raw.slice(4)}` : null;
    const explicit = /^(https?|ftp):\/\//i.test(raw);
    if (!explicit && /^[a-z][a-z\d+.-]*:/i.test(raw) && !/^[^/:]+:\d+(?:[/?#]|$)/.test(raw)) return null;
    try {
      const url = new URL(explicit ? raw : `https://${raw}`);
      if (!['http:', 'https:', 'ftp:'].includes(url.protocol) || url.username || url.password) return null;
      const host = url.hostname;
      if (host.startsWith('[') && host.endsWith(']')) return explicit ? url.href : null;
      const labels = host.replace(/\.$/, '').split('.');
      if (!labels.every(label => /^[a-z\d](?:[a-z\d-]{0,61}[a-z\d])?$/i.test(label)) || host.length > 253) return null;
      if (!explicit && !/^www\./i.test(host) && !this.KNOWN_TLDS.has(labels[labels.length - 1].toLowerCase())) return null;
      if (!explicit && labels.length < 2) return null;
      return url.href;
    } catch { return null; }
  }

  static findLinks(text: string): RichTextLink[] {
    const links: RichTextLink[] = [];
    const tokens = /\S+/g;
    let match: RegExpExecArray | null;
    while ((match = tokens.exec(text))) {
      let token = match[0];
      const leading = token.match(/^[([{\u201c\u2018"']+/)?.[0].length ?? 0;
      token = token.slice(leading).replace(/[.,;:!?\u201d\u2019"']+$/, '');
      for (const [open, close] of [['(', ')'], ['[', ']'], ['{', '}']]) {
        while (token.endsWith(close) && token.split(close).length > token.split(open).length) token = token.slice(0, -1);
      }
      token = token.replace(/[.,;:!?]+$/, '');
      const href = this.normalizeUrl(token);
      if (href) links.push({ start: match.index + leading, end: match.index + leading + token.length, text: token, href });
    }
    return links;
  }

  static plainTextHtml(text: string): string {
    const template = document.createElement('template');
    text.replace(/\r\n?/g, '\n').split('\n').forEach((line, i) => {
      if (i) template.content.appendChild(document.createElement('br'));
      template.content.appendChild(document.createTextNode(line));
    });
    return template.innerHTML;
  }

  static sanitizeHtml(html: string, wrapText = true, trimLeading = true): string {
    const source = document.createElement('template');
    const target = document.createElement('template');
    source.innerHTML = html ?? '';
    for (const node of Array.from(source.content.childNodes)) this.copyNode(node, target.content);
    if (trimLeading) this.removeLeadingEmptyNodes(target.content);
    if (wrapText) this.wrapRootText(target.content);
    if (trimLeading) this.normalizeSpaces(target.content);
    return target.innerHTML;
  }

  private static removeLeadingEmptyNodes(parent: Node, preserveIndent = false): boolean {
    for (const node of Array.from(parent.childNodes)) {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent ?? '';
        if (preserveIndent && /^[\s\u200b\u2060\ufeff]+$/.test(text)) {
          let next = node.nextSibling;
          while (next && !(next instanceof Element && ['BR', 'P', 'DIV', 'UL', 'OL', 'LI'].includes(next.tagName))) {
            if (next.textContent?.replace(/[\s\u200b\u2060\ufeff]/g, '')) return true;
            next = next.nextSibling;
          }
        }
        node.textContent = text.replace(preserveIndent ? /^[\u200b\u2060\ufeff]+/ : /^[\s\u200b\u2060\ufeff]+/, '');
        if (!node.textContent?.replace(/[\s\u200b\u2060\ufeff]/g, '')) node.textContent = '';
        if (node.textContent) return true;
      } else if (this.removeLeadingEmptyNodes(node, preserveIndent || node instanceof Element && ['P', 'DIV', 'LI'].includes(node.tagName))) return true;
      parent.removeChild(node);
    }
    return false;
  }

  private static normalizeSpaces(parent: Node): void {
    let line: Text[] = [];
    const flush = (): void => {
      if (line.every(node => !node.data.trim())) {
        line.forEach(node => node.data = '');
        line = [];
        return;
      }
      let leading = true;
      let pending: Text | null = null;
      let space = '';
      for (const node of line) {
        const text = node.data;
        node.data = '';
        for (const character of text) {
          if (character === ' ' || character === '\u00a0') {
            if (leading) node.data += character;
            else if (!pending) { pending = node; space = character; }
            continue;
          }
          if (character === '\n' || character === '\r') {
            pending = null;
            leading = true;
          } else {
            if (pending) pending.data += space;
            pending = null;
            if (character !== '\t') leading = false;
          }
          node.data += character;
        }
      }
      line = [];
    };
    const visit = (node: Node): void => {
      if (node instanceof Text) { line.push(node); return; }
      const boundary = node instanceof Element && ['P', 'DIV', 'BR', 'LI', 'UL', 'OL'].includes(node.tagName);
      if (boundary) flush();
      node.childNodes.forEach(visit);
      if (boundary) flush();
    };
    visit(parent);
    flush();
  }

  private static copyNode(node: Node, parent: Node): void {
    if (node.nodeType === Node.TEXT_NODE) {
      parent.appendChild(document.createTextNode(node.textContent ?? ''));
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const source = node as HTMLElement;
    if (this.DROP.has(source.tagName)) return;
    let tag = source.tagName;
    if (['H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'PRE', 'SECTION', 'ARTICLE'].includes(tag)) tag = 'DIV';
    const href = tag === 'A' ? this.normalizeUrl(source.getAttribute('href') ?? '', true) : null;
    if (!this.TAGS.has(tag) || (tag === 'A' && !href)) {
      let target = parent;
      for (const [property, value, name] of [['fontWeight', /^(bold|[6-9]00)$/, 'strong'], ['fontStyle', /^italic$/, 'em'], ['textDecoration', /underline/, 'u']] as const) {
        if (value.test(source.style[property])) {
          const wrapper = document.createElement(name);
          target.appendChild(wrapper);
          target = wrapper;
        }
      }
      for (const child of Array.from(node.childNodes)) this.copyNode(child, target);
      return;
    }
    const element = document.createElement(tag.toLowerCase());
    if (href) {
      element.setAttribute('href', href);
      if (!href.startsWith('/')) { element.setAttribute('target', '_blank'); element.setAttribute('rel', 'noopener noreferrer'); }
    }
    if (tag === 'OL' || tag === 'UL') {
      const classes = Array.from(source.classList).filter(name => this.LIST_CLASSES.has(name));
      if (classes.length) element.className = classes.join(' ');
      if (this.LIST_STYLES.has(source.style.listStyleType)) element.style.listStyleType = source.style.listStyleType;
      if (tag === 'OL' && /^[aAiI1]$/.test(source.getAttribute('type') ?? '')) element.setAttribute('type', source.getAttribute('type')!);
    }
    for (const attribute of tag === 'OL' ? ['start'] : tag === 'LI' ? ['value'] : []) {
      const value = source.getAttribute(attribute);
      if (value && /^-?\d{1,6}$/.test(value)) element.setAttribute(attribute, value);
    }
    if (tag === 'DIV' && source.classList.contains('rte-list-separator')) element.className = 'rte-list-separator';
    for (const child of Array.from(node.childNodes)) this.copyNode(child, element);
    parent.appendChild(element);
  }

  private static wrapRootText(root: DocumentFragment): void {
    let paragraph: HTMLElement | null = null;
    for (const node of Array.from(root.childNodes)) {
      if (node.nodeType === Node.ELEMENT_NODE && ['P', 'DIV', 'UL', 'OL'].includes((node as Element).tagName)) { paragraph = null; continue; }
      if (!paragraph) { paragraph = document.createElement('p'); root.insertBefore(paragraph, node); }
      paragraph.appendChild(node);
    }
  }

  static linkifyHtml(html: string): string {
    const template = document.createElement('template');
    // O fragmento pode ser colado depois de texto: seu prefixo vazio não é o início do documento.
    template.innerHTML = this.sanitizeHtml(html, false, false);
    const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT);
    const nodes: Text[] = [];
    let node: Node | null;
    while ((node = walker.nextNode())) {
      if (!(node.parentElement?.closest('a'))) nodes.push(node as Text);
    }
    for (const textNode of nodes) {
      const text = textNode.data;
      const links = this.findLinks(text);
      if (!links.length) continue;
      const fragment = document.createDocumentFragment();
      let offset = 0;
      for (const link of links) {
        fragment.appendChild(document.createTextNode(text.slice(offset, link.start)));
        const anchor = document.createElement('a');
        anchor.href = link.href; anchor.target = '_blank'; anchor.rel = 'noopener noreferrer'; anchor.textContent = link.text;
        fragment.appendChild(anchor); offset = link.end;
      }
      fragment.appendChild(document.createTextNode(text.slice(offset)));
      textNode.replaceWith(fragment);
    }
    return template.innerHTML;
  }

  static visibleText(html: string): string {
    const template = document.createElement('template');
    template.innerHTML = this.sanitizeHtml(html, false);
    const read = (node: Node): string => {
      if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? '';
      const content = Array.from(node.childNodes).map(read).join('');
      return node instanceof Element && ['P', 'DIV', 'BR', 'LI', 'UL', 'OL'].includes(node.tagName) ? `${content}\n` : content;
    };
    return read(template.content).replace(/[\u200b\u2060\ufeff]/g, '').replace(/\s+/g, ' ').trim();
  }

  static count(html: string): number { return Array.from(this.visibleText(html)).length; }
  static byteLength(html: string): number { return new TextEncoder().encode(html).length; }
}

interface RichTextLink { start: number; end: number; text: string; href: string; }
